'use client';

import React, { useMemo, useRef, useState } from 'react';

import {
  Button,
  Card,
  CardBody,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Select,
  SelectItem,
} from '@nextui-org/react';
import { Camera, ChevronDown, EyeOff, MoreVertical } from 'lucide-react';
import { Rate } from '@/components/ui/rate';
import { RelativeDate } from '@/components/ui/relative-date';

import { IMasterclass } from '@/entities/masterclass/types';
import { IReview } from '@/entities/review/types';
import { useIsAdmin } from '@/shared/hooks/is-admin';
import { declension } from '@/shared/utils/declecsion';
import { cn } from '@/shared/utils/utils';
import Image from 'next/image';
import 'react-photo-view/dist/react-photo-view.css';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import { Link } from '@/shared/navigation';
import { ROUTES } from '@/shared/routes/routes';
import { RejectReviewModalConfirm } from '@/widgets/me-reviews/review-card/review-card';

/** Сколько отзывов видно в свёрнутом состоянии */
const COLLAPSED_REVIEWS_COUNT = 3;

const hasPhotos = (review: IReview) => (review.images?.length ?? 0) > 0;

const ReviewItem = ({
  review,
  onUnpublish,
}: {
  review: IReview;
  /** Есть только у админа */
  onUnpublish?: () => void;
}) => {
  const displayName =
    review.authorName?.trim() || review.user?.username || 'Покупатель';
  return (
    <Card shadow="sm">
      <CardBody className="flex flex-col gap-2 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-small font-semibold text-foreground">
              {displayName}
            </p>
            <RelativeDate
              date={review.createdAt}
              className="block text-tiny text-default-500"
            />
          </div>
          <div className="flex shrink-0 items-start gap-1">
            <Rate value={review.rate} readOnly size={14} className="pt-0.5" />
            {onUnpublish && (
              <Dropdown placement="bottom-end">
                <DropdownTrigger>
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    radius="full"
                    aria-label="Действия с отзывом"
                    className="-mr-2 -mt-1.5 text-default-500"
                  >
                    <MoreVertical size={16} />
                  </Button>
                </DropdownTrigger>
                <DropdownMenu
                  aria-label="Действия с отзывом"
                  onAction={(key) => key === 'unpublish' && onUnpublish()}
                >
                  <DropdownItem
                    key="unpublish"
                    color="danger"
                    className="text-danger"
                    startContent={<EyeOff size={16} />}
                  >
                    Снять с публикации
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            )}
          </div>
        </div>
        <p className="whitespace-pre-line break-words text-small leading-relaxed text-default-700">
          {review.description}
        </p>
        {review.images?.length > 0 && (
          <PhotoProvider>
            <div className="flex flex-wrap gap-2 pt-1">
              {review.images.map((imageSrc) => (
                <PhotoView key={imageSrc} src={imageSrc}>
                  <Image
                    className="h-[72px] w-[72px] cursor-zoom-in rounded-lg object-cover"
                    src={imageSrc}
                    alt={`Фото отзыва — ${displayName}`}
                    width={72}
                    height={72}
                  />
                </PhotoView>
              ))}
            </div>
          </PhotoProvider>
        )}
      </CardBody>
    </Card>
  );
};

export const McReviewList = ({
  masterclass,
}: {
  masterclass?: IMasterclass;
}) => {
  const sectionRef = useRef<HTMLElement>(null);
  const [isExpanded, setExpanded] = useState(false);

  const handleCollapse = () => {
    setExpanded(false);
    // Если блок уже пролистан, после сворачивания возвращаем к началу отзывов
    const section = sectionRef.current;
    if (section && section.getBoundingClientRect().top < 0) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const [sortValue, setSortValue] = useState('newest');
  const [onlyWithPhotos, setOnlyWithPhotos] = useState(false);

  const { isAdmin } = useIsAdmin();
  const [unpublishModal, setUnpublishModal] = useState<{
    reviewId: number;
    isOpen: boolean;
  } | null>(null);
  const closeUnpublishModal = () =>
    setUnpublishModal((current) => current && { ...current, isOpen: false });

  const renderReview = (item: IReview) => (
    <ReviewItem
      key={item.id}
      review={item}
      onUnpublish={
        isAdmin
          ? () => setUnpublishModal({ reviewId: item.id, isOpen: true })
          : undefined
      }
    />
  );

  const reviewsWithPhotosCount = useMemo(
    () => (masterclass?.reviews ?? []).filter(hasPhotos).length,
    [masterclass?.reviews],
  );

  const sortSelectItems = useMemo(() => {
    return [
      {
        key: 'newest',
        label: 'По дате ↓',
      },
      {
        key: 'oldest',
        label: 'По дате ↑',
      },
      {
        key: 'rating desc',
        label: 'По рейтингу ↓',
      },
      {
        key: 'rating asc',
        label: 'По рейтингу ↑',
      },
    ];
  }, []);

  const sortedReviews = useMemo(() => {
    const allReviews = masterclass?.reviews || [];
    const reviews = onlyWithPhotos ? allReviews.filter(hasPhotos) : allReviews;
    switch (sortValue) {
      case 'newest':
        return [...reviews].sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
      case 'oldest':
        return [...reviews].sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
      case 'rating desc':
        return [...reviews].sort((a, b) => (b.rate || 0) - (a.rate || 0));
      case 'rating asc':
        return [...reviews].sort((a, b) => (a.rate || 0) - (b.rate || 0));
      default:
        return reviews;
    }
  }, [masterclass?.reviews, sortValue, onlyWithPhotos]);

  const visibleReviews = sortedReviews.slice(0, COLLAPSED_REVIEWS_COUNT);
  const hiddenReviews = sortedReviews.slice(COLLAPSED_REVIEWS_COUNT);
  const hasReviews = (masterclass?.reviews?.length ?? 0) > 0;
  const addReviewHref = masterclass?.wordId
    ? `/masterclasses/${masterclass.wordId}/add-review`
    : ROUTES.masterclasses;

  return (
    <section
      ref={sectionRef}
      className="col-span-12 flex flex-col gap-4 mt-8 scroll-mt-24"
    >
      <div className="w-full justify-between gap-2 flex flex-wrap">
        <div className="flex items-center gap-2">
          <h2 className="text-medium font-semibold md:text-large">Отзывы</h2>
          {hasReviews && (
            <div className="flex items-center gap-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                xmlnsXlink="http://www.w3.org/1999/xlink"
                aria-hidden="true"
                role="img"
                className="text-warning-500 iconify iconify--solar"
                width="20"
                height="20"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M9.153 5.408C10.42 3.136 11.053 2 12 2s1.58 1.136 2.847 3.408l.328.588c.36.646.54.969.82 1.182s.63.292 1.33.45l.636.144c2.46.557 3.689.835 3.982 1.776c.292.94-.546 1.921-2.223 3.882l-.434.507c-.476.557-.715.836-.822 1.18c-.107.345-.071.717.001 1.46l.066.677c.253 2.617.38 3.925-.386 4.506s-1.918.051-4.22-1.009l-.597-.274c-.654-.302-.981-.452-1.328-.452s-.674.15-1.328.452l-.596.274c-2.303 1.06-3.455 1.59-4.22 1.01c-.767-.582-.64-1.89-.387-4.507l.066-.676c.072-.744.108-1.116 0-1.46c-.106-.345-.345-.624-.821-1.18l-.434-.508c-1.677-1.96-2.515-2.941-2.223-3.882S3.58 8.328 6.04 7.772l.636-.144c.699-.158 1.048-.237 1.329-.45s.46-.536.82-1.182z"
                ></path>
              </svg>
              <span className="text-medium font-semibold md:text-large">
                {masterclass?.rate?.toFixed(1) ?? '0.0'}
              </span>
              <span className="text-right sm:flex hidden text-small text-default-500 lg:text-medium">
                {declension(masterclass?.reviewsCount ?? 0, [
                  'отзыв',
                  'отзыва',
                  'отзывов',
                ])}
              </span>
            </div>
          )}
        </div>
        {hasReviews && (
          <div className="flex flex-wrap items-center gap-2">
            {reviewsWithPhotosCount > 0 && (
              <Button
                size="sm"
                radius="full"
                variant={onlyWithPhotos ? 'solid' : 'bordered'}
                color={onlyWithPhotos ? 'primary' : 'default'}
                aria-pressed={onlyWithPhotos}
                startContent={<Camera size={14} />}
                onPress={() => setOnlyWithPhotos((value) => !value)}
              >
                С фото ({reviewsWithPhotosCount})
              </Button>
            )}
            <Select
              value={sortValue}
              onChange={(e) => setSortValue(e.target.value)}
              size="sm"
              radius="full"
              label="Сортировка"
              defaultSelectedKeys={['newest']}
              className="w-[180px]"
            >
              {sortSelectItems.map((item) => (
                <SelectItem key={item.key}>{item.label}</SelectItem>
              ))}
            </Select>
          </div>
        )}
      </div>

      {!hasReviews && (
        <Card className="border border-dashed border-default-300 bg-default-50/50">
          <CardBody className="flex flex-col gap-4 py-8 px-6">
            <p className="text-lg font-medium text-foreground">
              Пока нет отзывов
            </p>
            <p className="text-default-600 text-sm md:text-base">
              Оставьте отзыв: после одобрения модератором на указанный email
              придёт промокод на 10% скидки. Если отзыв не опубликуют — пришлём
              письмо с причиной.
            </p>
            <div>
              <Button
                as={Link}
                href={addReviewHref}
                color="primary"
                radius="full"
              >
                Оставить отзыв
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {hasReviews && (
        <div className="flex flex-col">
          <div className="flex flex-col gap-3">
            {visibleReviews.map(renderReview)}
          </div>

          {hiddenReviews.length > 0 && (
            <>
              {/* grid-rows 0fr → 1fr плавно анимирует высоту до auto; min-h оставляет «выглядывающий» край следующего отзыва.
                  Режем только по вертикали (overflow-y: clip), чтобы не срезать боковые тени карточек; pb-3 — место под нижнюю тень */}
              <div
                className={cn(
                  'relative grid transition-[grid-template-rows] duration-500 ease-in-out motion-reduce:transition-none',
                  isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                )}
              >
                <div className="min-h-[88px] overflow-x-visible overflow-y-clip">
                  <div className="flex flex-col gap-3 pt-3 pb-3">
                    {hiddenReviews.map(renderReview)}
                  </div>
                </div>
                <div
                  aria-hidden
                  onClick={() => setExpanded(true)}
                  className={cn(
                    'absolute -inset-x-3 inset-y-0 bg-gradient-to-b from-transparent to-background transition-opacity duration-500',
                    isExpanded
                      ? 'pointer-events-none opacity-0'
                      : 'cursor-pointer opacity-100',
                  )}
                />
              </div>

              <div className="flex justify-center mt-1">
                <Button
                  variant="flat"
                  radius="full"
                  aria-expanded={isExpanded}
                  onPress={isExpanded ? handleCollapse : () => setExpanded(true)}
                  endContent={
                    <ChevronDown
                      size={16}
                      className={cn(
                        'transition-transform duration-300',
                        isExpanded && 'rotate-180',
                      )}
                    />
                  }
                >
                  {isExpanded
                    ? 'Скрыть'
                    : `Показать все отзывы (${sortedReviews.length})`}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {isAdmin && (
        <RejectReviewModalConfirm
          // key сбрасывает введённую причину при переходе к другому отзыву
          key={unpublishModal?.reviewId}
          reviewId={unpublishModal?.reviewId ?? 0}
          isOpen={!!unpublishModal?.isOpen}
          close={closeUnpublishModal}
          onOpenChange={closeUnpublishModal}
          title="Снять отзыв с публикации"
          submitLabel="Снять с публикации"
        />
      )}
    </section>
  );
};
